/**
 * `useMe()` — react-query wrapper around `fetchMe()` so any UI surface
 * (the Header's org-name chrome, Settings, etc.) can read the
 * `/api/me` response without re-implementing fetch + caching.
 *
 * `fetchMe()` itself has a module-level session cache; react-query adds
 * the React-tree subscription so consumers re-render on data arrival
 * (instead of waiting for a manual setState). Stale-time = 5 min:
 * `/api/me` rarely changes mid-session (locale / displayName / org
 * name are stable), and PATCH /api/me already invalidates the fetchMe
 * cache directly.
 *
 * **Known cache-coherence gap (deferred):** the PATCH path in
 * `lexserver-me.ts` invalidates the *fetchMe module cache* but does
 * NOT call `queryClient.invalidateQueries({ queryKey: ["me"] })` on
 * the react-query cache. So a Settings PATCH that mutates
 * `displayName` / `locale` won't propagate to useMe consumers until
 * the 5-min staleTime elapses. Today's only useMe consumer (the
 * Header reading `org.name` — immutable from the client) is
 * unaffected. Future consumers reading mutable fields should: (a)
 * subscribe via `useMe()` AND (b) invalidate the `["me"]` query from
 * their PATCH handler — or this hook should be wired to do it.
 */

import { useQuery } from "@tanstack/react-query";
import { fetchMe, type MeResponse } from "@/lib/lexserver-me";

export function useMe() {
  return useQuery<MeResponse, Error>({
    queryKey: ["me"],
    queryFn: fetchMe,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}
